import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './NotFound.module.css';

const NotFound = () => {
  const canvasRef = useRef(null);
  const navigate = useNavigate();
  const requestRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const size = 380;
    canvas.width = size;
    canvas.height = size;

    let time = 0;

    // organic wobble for sketchy feel
    const wobble = (seed, intensity = 1.0) => {
      return (Math.sin(time * 4.7 + seed) * 0.8 + Math.cos(time * 5.3 + seed * 1.2) * 0.7) * intensity;
    };

    // ---------- DOODLE DRAWING FUNCTIONS ----------

    const drawBrokenPlug = (cx, cy) => {
      ctx.save();
      ctx.shadowBlur = 3;
      ctx.shadowColor = "#CC8244";
      
      ctx.beginPath();
      const w = 64, h = 88;
      const left = cx - w/2;
      const top = cy - h/2;
      ctx.moveTo(left + 4 + wobble(1)*1.2, top + wobble(2)*0.8);
      ctx.lineTo(left + w - 5 + wobble(3), top + wobble(4));
      ctx.lineTo(left + w - 3 + wobble(5), top + h - 5 + wobble(6));
      ctx.lineTo(left + 4 + wobble(7), top + h - 3 + wobble(8));
      ctx.closePath();
      ctx.lineWidth = 2.2;
      ctx.strokeStyle = "#BB6731";
      ctx.stroke();
      ctx.fillStyle = "#FFF2E3cc";
      ctx.fill();
      
      const eyeY = top + 34;
      ctx.beginPath();
      ctx.ellipse(cx - 16 + wobble(9)*1, eyeY + wobble(10), 7, 8, 0, 0, Math.PI*2);
      ctx.fillStyle = "#E0B07A";
      ctx.fill();
      ctx.strokeStyle = "#9C5629";
      ctx.stroke();
      ctx.beginPath();
      ctx.ellipse(cx + 16 + wobble(11)*0.8, eyeY + wobble(12), 7, 8, 0, 0, Math.PI*2);
      ctx.fill();
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo(cx - 14 + wobble(13)*0.5, top + 52);
      ctx.lineTo(cx - 6 + wobble(14), top + 68);
      ctx.lineTo(cx + 3 + wobble(15), top + 60);
      ctx.lineTo(cx + 14 + wobble(16), top + 78);
      ctx.lineWidth = 2.6;
      ctx.strokeStyle = "#C56C38";
      ctx.setLineDash([6, 5]);
      ctx.stroke();
      ctx.setLineDash([]);
      
      ctx.beginPath();
      ctx.arc(cx - 28, top + 72, 2.8 + Math.sin(time*14)*1, 0, Math.PI*2);
      ctx.fillStyle = `rgba(255, 145, 60, 0.9)`;
      ctx.fill();
      ctx.restore();
    };

    const drawWeakBattery = (cx, cy) => {
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(Math.sin(time*0.8)*0.04);
      ctx.beginPath();
      ctx.moveTo(-28, -12);
      ctx.lineTo(28, -12);
      ctx.lineTo(32, -4);
      ctx.lineTo(32, 16);
      ctx.lineTo(-32, 16);
      ctx.lineTo(-32, -4);
      ctx.closePath();
      ctx.lineWidth = 2;
      ctx.strokeStyle = "#D47A3A";
      ctx.stroke();
      ctx.fillStyle = "#FFF0E0";
      ctx.fill();
      for(let i=0; i<3; i++) {
        let fillH = 2 + (i===2 ? 2 : 5);
        let opacity = 0.45 + Math.sin(time*5 + i)*0.2;
        ctx.fillStyle = `rgba(210, 100, 40, ${opacity})`;
        ctx.fillRect(-22, 2 + i*7, 44, fillH);
      }
      for(let s=0; s<4; s++) {
        let angle = time*7 + s*1.8;
        let rad = 30 + Math.sin(time*12+s)*3;
        let xp = Math.cos(angle)*rad;
        let yp = Math.sin(angle*1.2)*rad*0.7;
        ctx.beginPath();
        ctx.arc(xp, yp, 1.8 + Math.sin(time*20+s), 0, Math.PI*2);
        ctx.fillStyle = `rgba(255, 150, 50, 0.85)`;
        ctx.fill();
      }
      ctx.restore();
    };

    const errorParticles = (cx, cy, radius) => {
      for(let i=0; i<14; i++) {
        let ang = (i/14)*Math.PI*2 + time*2.5;
        let rVar = radius + 16 + Math.sin(time*5 + i)*6;
        let x = cx + Math.cos(ang) * rVar;
        let y = cy + Math.sin(ang*0.92) * rVar * 0.88;
        let flick = (Math.sin(time*15 + i)+1)/2;
        if(flick > 0.55) {
          ctx.beginPath();
          ctx.arc(x, y, 2 + flick*1.4, 0, Math.PI*2);
          ctx.fillStyle = `rgba(245, 135, 55, ${0.6+flick*0.3})`;
          ctx.fill();
          ctx.beginPath();
          ctx.moveTo(x-3.5,y); ctx.lineTo(x+3.5,y);
          ctx.moveTo(x,y-3.5); ctx.lineTo(x,y+3.5);
          ctx.lineWidth = 1;
          ctx.strokeStyle = "#FFB87A";
          ctx.stroke();
        }
      }
    };

    const drawMain404 = (cx, cy) => {
      ctx.font = `bold ${58 + Math.sin(time*3)*2}px "Outfit", "Comic Neue", monospace`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "#B56128";
      ctx.fillText("404", cx+3, cy+4);
      ctx.fillStyle = "#F59C52";
      ctx.fillText("404", cx, cy);
      ctx.beginPath();
      for(let u=0; u<32; u++) {
        let xp = cx - 68 + u*4.3;
        let yp = cy + 42 + Math.sin(u*0.75 + time*8)*2.2;
        if(u===0) ctx.moveTo(xp, yp);
        else ctx.lineTo(xp, yp);
      }
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = "#E88E48";
      ctx.stroke();
      ctx.font = `bold 12px "Courier New", monospace`;
      ctx.fillStyle = "#BE733C";
      ctx.fillText("> PAGE VAPORIZED <", cx, cy + 66);
    };

    const tornRing = (cx, cy, radius) => {
      ctx.save();
      ctx.shadowBlur = 4;
      for(let l=0; l<2; l++) {
        ctx.beginPath();
        for(let a=0; a<=Math.PI*2; a+=0.12) {
          let tear = Math.sin(a * 2.4 + time*6) * 5.2;
          let rNow = radius + tear + Math.sin(a*4 - time*2.5)*2;
          let x = cx + Math.cos(a + time*0.4) * rNow;
          let y = cy + Math.sin(a*0.96) * rNow;
          if(a===0) ctx.moveTo(x,y);
          else ctx.lineTo(x,y);
        }
        ctx.closePath();
        ctx.lineWidth = 2.3;
        ctx.strokeStyle = l===0 ? "#CB783A" : "rgba(220,125,50,0.7)";
        ctx.stroke();
      }
      ctx.beginPath();
      ctx.moveTo(cx - radius*0.5, cy - radius*0.25);
      ctx.lineTo(cx + radius*0.55, cy + radius*0.55);
      ctx.lineWidth = 3.2;
      ctx.setLineDash([7, 6]);
      ctx.strokeStyle = "#E17A38";
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    };

    const energyGhosts = (cx, cy, maxR) => {
      ctx.save();
      ctx.globalAlpha = 0.5;
      for(let g=0; g<3; g++) {
        ctx.beginPath();
        for(let ang=0; ang<=Math.PI*1.8; ang+=0.18) {
          let rWave = maxR + 13 + Math.sin(ang*4 + time*7)*4;
          let x = cx + Math.cos(ang + time*g) * rWave;
          let y = cy + Math.sin(ang*0.88) * rWave*0.85;
          if(ang===0) ctx.moveTo(x,y);
          else ctx.lineTo(x,y);
        }
        ctx.strokeStyle = `rgba(215, 105, 40, 0.55)`;
        ctx.lineWidth = 1.8;
        ctx.stroke();
      }
      ctx.restore();
    };

    const drawSkinBg = () => {
      ctx.fillStyle = "#FFF7ED";
      ctx.fillRect(0, 0, size, size);
      for(let i=0; i<180; i++) {
        ctx.beginPath();
        ctx.moveTo(i*7, 0);
        ctx.lineTo(i*7+3, size);
        ctx.lineTo(i*7-3, size);
        ctx.fillStyle = `rgba(180,100,50,0.038)`;
        ctx.fill();
      }
      for(let y=0; y<size; y+=16) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(size, y+5);
        ctx.lineWidth = 0.5;
        ctx.strokeStyle = "#D8B48C30";
        ctx.stroke();
      }
    };

    const render = () => {
      ctx.clearRect(0,0,size,size);
      drawSkinBg();
      const cx = size/2;
      const cy = size/2 - 6;
      const ringR = 98;
      tornRing(cx, cy, ringR);
      drawWeakBattery(cx - 84, cy - 28);
      drawBrokenPlug(cx + 76, cy + 12);
      energyGhosts(cx, cy, ringR+6);
      errorParticles(cx, cy, ringR-2);
      drawMain404(cx, cy + 22);
      ctx.beginPath();
      ctx.moveTo(cx-32 + wobble(44)*1.2, cy-16 + wobble(45));
      ctx.lineTo(cx+32 + wobble(46), cy+14 + wobble(47));
      ctx.moveTo(cx+30 + wobble(48), cy-18 + wobble(49));
      ctx.lineTo(cx-30 + wobble(50), cy+16 + wobble(51));
      ctx.lineWidth = 2;
      ctx.strokeStyle = "#CC7940";
      ctx.setLineDash([5, 6]);
      ctx.stroke();
      ctx.setLineDash([]);
      for(let n=0; n<12; n++) {
        let angN = (n/12)*Math.PI*2 + time*2.3;
        let radN = ringR - 3 + Math.sin(time*6 + n)*3;
        let xn = cx + Math.cos(angN) * radN;
        let yn = cy + Math.sin(angN) * radN*0.95;
        let pulse = (Math.sin(time*16 + n)+1)/2;
        if(pulse > 0.6) {
          ctx.beginPath();
          ctx.arc(xn, yn, 2.2 + pulse*1, 0, Math.PI*2);
          ctx.fillStyle = `rgba(255, 140, 50, 0.9)`;
          ctx.fill();
          ctx.beginPath();
          ctx.moveTo(xn-4, yn); ctx.lineTo(xn+4, yn);
          ctx.moveTo(xn, yn-4); ctx.lineTo(xn, yn+4);
          ctx.lineWidth = 1.2;
          ctx.strokeStyle = "#FFB368";
          ctx.stroke();
        }
      }
      for(let f=0; f<22; f++) {
        let angF = f*0.85 + time*2.9;
        let radF = ringR + 26 + Math.sin(time*4.5+f)*8;
        let xF = cx + Math.cos(angF) * radF;
        let yF = cy + Math.sin(angF*0.86) * radF * 0.84;
        let flick = (Math.sin(time*21 + f)+1)/2;
        if(flick > 0.6) {
          ctx.beginPath();
          ctx.arc(xF, yF, 1.5 + flick*1.2, 0, Math.PI*2);
          ctx.fillStyle = `rgba(255, 155, 60, 0.85)`;
          ctx.fill();
        }
      }
      ctx.beginPath();
      ctx.setLineDash([6, 5, 2, 4]);
      for(let a=0; a<=Math.PI*2; a+=0.22) {
        let rB = ringR + 50 + Math.sin(a*3 + time*5.5)*3.5;
        let xb = cx + Math.cos(a + time*0.6) * rB;
        let yb = cy + Math.sin(a*0.94) * rB*0.93;
        if(a===0) ctx.moveTo(xb, yb);
        else ctx.lineTo(xb, yb);
      }
      ctx.closePath();
      ctx.strokeStyle = "#D88844";
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.setLineDash([]);
    };

    const animate = (timestamp) => {
      time = (timestamp / 1000) * 1.5;
      render();
      requestRef.current = requestAnimationFrame(animate);
    };

    requestRef.current = requestAnimationFrame(animate);

    return () => {
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
  }, []);

  const handleZapEffect = () => {
    // Local speed boost simulation
    // In React we can just let the animation loop handle it if we have a state, 
    // but here we can just let the user click and see the jitter.
  };

  return (
    <div className={styles.notFoundBody}>
      <div className={styles.errorContainer}>
        <canvas 
          ref={canvasRef} 
          className={styles.doodleCanvas}
          onClick={handleZapEffect}
        ></canvas>
        
        <div className={styles.big404}>404</div>
        <div className={styles.subMessage}>
          ⚡ electrical current lost • page not found ⚡
        </div>
        <div className={styles.buttonGroup}>
          <button className={styles.doodleBtn} onClick={() => navigate('/')}>
            ⟵ GO HOME
          </button>
          <button className={`${styles.doodleBtn} ${styles.outline}`} onClick={() => window.location.reload()}>
            ⚡ ZAP RETRY
          </button>
        </div>
        <footer className={styles.footer}> Pencil doodle spark • check your circuit path ✨</footer>
      </div>
    </div>
  );
};

export default NotFound;
